import { useEffect, useRef, useState } from "react";
import AppShell from "@/components/feature/AppShell";
import { guideSuggestions } from "@/mocks/vivelo";
import { fetchCurrentProfile } from "@/services/auth";
import { askGuiaIa, type GuiaChatMessage } from "@/services/guiaIa";

type Message = { role: "user" | "ai"; text: string; isError?: boolean };

function greeting(firstName: string | null): Message {
  const hello = firstName ? `¡Hola, ${firstName}!` : "¡Hola!";
  return {
    role: "ai",
    text: `${hello} Soy tu Guía IA de Vívelo. Estoy aquí para ayudarte a prospectar, generar openers, sugerir seguimientos y acompañarte en tu camino de crecimiento. ¿Por dónde empezamos?`,
  };
}

export default function GuiaIA() {
  const [messages, setMessages] = useState<Message[]>([greeting(null)]);
  const bottomRef = useRef<HTMLDivElement>(null);

  // Saluda con el nombre real del usuario (solo si todavía no escribió nada).
  useEffect(() => {
    let active = true;
    fetchCurrentProfile().then((profile) => {
      if (!active || !profile?.fullName) return;
      const firstName = profile.fullName.split(" ")[0];
      setMessages((prev) => (prev.length === 1 ? [greeting(firstName)] : prev));
    });
    return () => {
      active = false;
    };
  }, []);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [listening, setListening] = useState(false);

  const send = async (text: string) => {
    const clean = text.trim();
    if (!clean || loading) return;
    const next: Message[] = [...messages, { role: "user", text: clean }];
    setMessages(next);
    setInput("");
    setLoading(true);

    // Historial real para la IA: sin el saludo inicial ni mensajes de error.
    const history: GuiaChatMessage[] = next
      .slice(1)
      .filter((m) => !m.isError)
      .map((m) => ({ role: m.role === "user" ? "user" : "assistant", content: m.text }));

    const result = await askGuiaIa(history);
    setMessages((prev) => [
      ...prev,
      "reply" in result
        ? { role: "ai", text: result.reply }
        : { role: "ai", text: result.message, isError: true },
    ]);
    setLoading(false);
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages, loading]);

  return (
    <AppShell headerVariant="onLight">
      <div className="max-w-3xl mx-auto px-4 py-6 flex flex-col gap-4" style={{ minHeight: "calc(100vh - 140px)" }}>
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-accent-500/15 flex items-center justify-center">
            <i className="ri-robot-2-fill text-2xl text-accent-600"></i>
          </div>
          <div>
            <h1 className="font-heading font-bold text-foreground-950 text-xl">Guía IA</h1>
            <div className="flex items-center gap-1 text-xs text-primary-600">
              <span className="w-2 h-2 rounded-full bg-primary-500 inline-block"></span>
              En línea · Tu asistente personalizado
            </div>
          </div>
        </div>

        {/* Suggestions */}
        <div>
          <div className="text-xs text-foreground-500 mb-2">Sugerencias para ti:</div>
          <div className="flex flex-wrap gap-2">
            {guideSuggestions.map((s, i) => (
              <button
                key={i}
                onClick={() => send(s)}
                className="text-sm bg-primary-500/10 text-primary-700 border border-primary-500/25 rounded-full px-3 py-1.5 hover:bg-primary-500/20 cursor-pointer whitespace-nowrap"
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {/* Messages */}
        <div className="flex-1 flex flex-col gap-3">
          {messages.map((m, i) => (
            <div key={i} className={`flex items-end gap-3 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
              {m.role === "ai" ? (
                <span className="w-9 h-9 rounded-full bg-accent-500/15 flex-shrink-0 flex items-center justify-center">
                  <i className="ri-robot-2-fill text-accent-600"></i>
                </span>
              ) : (
                <span className="w-9 h-9 rounded-full bg-primary-500/15 flex-shrink-0 flex items-center justify-center">
                  <i className="ri-user-3-fill text-primary-600"></i>
                </span>
              )}
              <div
                className={[
                  "max-w-[80%] rounded-2xl px-4 py-3 text-sm whitespace-pre-wrap",
                  m.role === "ai"
                    ? m.isError
                      ? "bg-accent-500/10 border border-accent-500/30 text-accent-700 rounded-bl-sm"
                      : "bg-background-100 border border-background-200 text-foreground-950 rounded-bl-sm"
                    : "bg-primary-500 text-background-950 rounded-br-sm font-medium",
                ].join(" ")}
              >
                {m.text}
              </div>
            </div>
          ))}
          {loading ? (
            <div className="flex items-center gap-3">
              <span className="w-9 h-9 rounded-full bg-accent-500/15 flex items-center justify-center">
                <i className="ri-robot-2-fill text-accent-600"></i>
              </span>
              <div className="bg-background-100 border border-background-200 rounded-2xl rounded-bl-sm px-5 py-3 flex gap-1.5 items-center">
                <span className="w-2 h-2 rounded-full bg-accent-400 animate-bounce" style={{ animationDelay: "0s" }}></span>
                <span className="w-2 h-2 rounded-full bg-accent-400 animate-bounce" style={{ animationDelay: "0.15s" }}></span>
                <span className="w-2 h-2 rounded-full bg-accent-400 animate-bounce" style={{ animationDelay: "0.3s" }}></span>
              </div>
            </div>
          ) : null}
          <div ref={bottomRef} />
        </div>

        {/* Input */}
        {listening ? (
          <div className="flex items-center gap-3 bg-accent-500/10 border border-accent-500/30 rounded-2xl px-4 py-3 vv-anim-rise">
            <span className="w-8 h-8 rounded-full bg-accent-500/20 flex items-center justify-center">
              <i className="ri-mic-fill text-accent-600 animate-pulse"></i>
            </span>
            <div className="flex-1">
              <div className="text-sm font-semibold text-foreground-950">Escuchando…</div>
              <div className="text-xs text-foreground-500">El asistente de voz estará disponible muy pronto.</div>
            </div>
            <button
              type="button"
              onClick={() => setListening(false)}
              className="text-xs font-semibold text-accent-700 cursor-pointer whitespace-nowrap"
            >
              Detener
            </button>
          </div>
        ) : null}

        <form
          onSubmit={(e) => { e.preventDefault(); send(input); }}
          className="flex items-center gap-3 bg-background-100 border border-background-200 rounded-2xl px-4 py-3"
        >
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            type="text"
            placeholder="Pregunta lo que quieras a tu Guía IA..."
            className="flex-1 bg-transparent outline-none text-sm text-foreground-950 placeholder:text-foreground-400"
          />
          <button
            type="button"
            onClick={() => setListening((v) => !v)}
            aria-label="Hablar con la Guía IA"
            title="Entrada de voz (próximamente)"
            className={[
              "w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer transition-colors",
              listening ? "bg-accent-500 text-white" : "bg-background-100 text-foreground-600 hover:bg-background-200",
            ].join(" ")}
          >
            <i className={`${listening ? "ri-mic-fill" : "ri-mic-line"} text-lg`}></i>
          </button>
          <button
            type="submit"
            disabled={!input.trim() || loading}
            aria-label="Enviar"
            className="w-10 h-10 rounded-xl bg-primary-500 text-background-950 flex items-center justify-center disabled:opacity-50 cursor-pointer"
          >
            <i className="ri-send-plane-fill text-lg"></i>
          </button>
        </form>
      </div>
    </AppShell>
  );
}